import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../utils/api";

function Toast({ order, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(onClose, 10000); // Auto-dismiss after 10 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-2xl p-4 max-w-sm animate-slide-in">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
          <span className="text-white text-xl">🎉</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-gray-900 text-sm mb-1">New Order!</div>
          <div className="text-xs text-gray-600 mb-2">
            {order.user?.name || "Customer"} placed an order
          </div>
          <div className="text-sm font-bold text-emerald-600">
            ${Number(order.total || 0).toFixed(2)}
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <button
        onClick={() => {
          navigate("/shop/orders");
          onClose();
        }}
        className="mt-3 w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors"
      >
        View Order →
      </button>
    </div>
  );
}

export default function OrderNotifications() {
  const [notifications, setNotifications] = useState([]);
  const lastCheckRef = useRef(Date.now());

  useEffect(() => {
    let mounted = true;
    let interval;

    const checkNewOrders = async () => {
      try {
        const now = Date.now();
        const since = new Date(lastCheckRef.current).toISOString();
        
        // Fetch orders since last check
        const result = await apiGet(`/api/shop/orders?since=${since}&limit=5`);
        const newOrders = Array.isArray(result?.orders) ? result.orders : [];

        if (mounted && newOrders.length > 0) {
          // Add new orders as notifications
          newOrders.forEach((order) => {
            setNotifications((prev) => [
              ...prev,
              { id: order._id, order, timestamp: Date.now() }
            ]);
          });

          // Play notification sound (optional)
          try {
            const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjGJ0fPTgjMGHm7A7+OZRQ0PVK3n77BdGAg+lt"); 
            audio.play().catch(() => {}); // Ignore errors if audio play is blocked
          } catch {}
        }

        lastCheckRef.current = now;
      } catch (err) {
        console.error("Failed to check new orders:", err);
      }
    };

    // Check immediately on mount
    checkNewOrders();

    // Then check every 30 seconds
    interval = setInterval(checkNewOrders, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-[200] space-y-3 max-w-sm">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          order={notification.order}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}
